import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  flow,
  groupBy,
  map,
  mapValues,
  size,
  sortBy,
  toPairs,
  uniqBy,
} from 'lodash/fp'

import { BrandType, GoodType } from '@monorepo/interfaces'
import { useQueryState } from '@monorepo/hooks'
import {
  OptionsType,
  RemoteFilterType,
  suggestionsSelector,
  resultSelector,
  getGroupResultSelector,
} from '@monorepo/deprecated/core/features/SearchEngine'

import { OfferType } from '@monorepo/interfaces'

const getGoods = flow([map('good'), uniqBy('id'), sortBy('name')])
const getBrands = flow([map('good.brand'), uniqBy('id'), sortBy('name')])
const getBrandsMeta = flow([
  groupBy('good.brandName'),
  mapValues(size),
  toPairs,
  map(([name, amount]) => ({ name, amount })),
  sortBy('name'),
])

type ResultType = {
  good: GoodType
  offers: OfferType[]
  meta: {
    isHasMoreOffers: boolean
    isExpanded: boolean
    totalOffers: number
  }
}

type FullResultType<T = ResultType> = {
  good?: ResultType
  crosses: ResultType[]
  originalCrosses: ResultType[]
}

export function useResult() {
  const {
    state: { sortBy, excludeBrands },
    updateState,
  } = useQueryState({
    brandName: undefined,
    number: undefined,
    crosses: true,
    sortBy: undefined,
    excludeBrands: undefined,
  } as Partial<RemoteFilterType & OptionsType>)

  const { data: suggestions } = useSelector(suggestionsSelector)
  const {
    data: { offers },
  } = useSelector(resultSelector)

  const getResult = useSelector(getGroupResultSelector)

  const result = useMemo(() => getResult({ sortBy, excludeBrands }), [
    excludeBrands,
    getResult,
    sortBy,
  ])

  const changeSortHandler = useCallback(
    (sortBy: string) => {
      updateState('sortBy', sortBy)
    },
    [updateState]
  )

  const changeExcludeBrandsHandler = useCallback(
    (excludeBrands: string[]) => {
      updateState('excludeBrands', excludeBrands)
    },
    [updateState]
  )

  const goods = useMemo<GoodType[]>(() => getGoods(offers), [offers])
  const brands = useMemo<BrandType[]>(() => getBrands(offers), [offers])
  const brandsMeta = useMemo<{ name: string; amount: number }[]>(
    () => getBrandsMeta(offers),
    [offers]
  )

  const totalOffers = offers.length
  const totalGoods = goods.length

  return {
    totalOffers,
    totalGoods,
    brands,
    brandsMeta,
    suggestions,
    result,
    changeSortHandler,
    changeExcludeBrandsHandler,
  }
}
